import TrackPlayer, { State } from "react-native-track-player";
import { usePlaybackStateStore, usePlaylistStateStore } from "../store";
import { getUUID } from "../utils";
import { PlaybackEventNames, ContentEventNames, } from "./interfaces/playback";
import { AnalyticsEventEmitter } from "./AnalyticsEventEmitter";
import { FileType } from "../enums";
class PlayerAnalytics {
    playingEventInterval = (process.env.ANALYTICS_HEARTBEAT &&
        Number(process.env.ANALYTICS_HEARTBEAT)) ||
        30000;
    async emitStartedEvent(timestamp, assetIds) {
        try {
            const { sessionId, activePlaylistItems } = usePlaylistStateStore.getState();
            if (!assetIds?.length) {
                assetIds = activePlaylistItems?.map(String);
            }
            if (!assetIds?.length) {
                console.warn("No asset ids for PlaybackEvents.started");
                return;
            }
            await this.emitEvent({
                event: PlaybackEventNames.started,
                timestamp,
                properties: {
                    session_id: sessionId,
                    content_asset_ids: assetIds,
                },
            });
        }
        catch (err) {
            console.error(`Failed to emit PlaybackEvents.started`, err);
        }
    }
    async emitPlaybackEvent(k, { timestamp, ...properties } = {}) {
        try {
            const event = {
                event: PlaybackEventNames[k],
                timestamp: timestamp || new Date(),
                properties: {
                    ...(await this.getPlaybackInfo()),
                    ...properties,
                },
            };
            if (!event.properties.content_asset_id) {
                console.warn(`No asset id for PlaybackEvents.${k}`);
                return;
            }
            await this.emitEvent(event);
        }
        catch (err) {
            console.error(`Failed to emit PlaybackEvents.${k}`, err);
        }
    }
    async emitAudioEvent(k, { timestamp, ...properties } = {}) {
        try {
            const eventData = {
                timestamp: timestamp || new Date(),
                ...(await this.getAudioInfo()),
                ...properties,
            };
            if (!eventData.position) {
                const progress = await TrackPlayer.getProgress();
                eventData.position = progress.position;
            }
            return await this.emitContentEvent(k, eventData);
        }
        catch (err) {
            console.error(`Failed to emit ContentEvents.${k}`, err);
        }
    }
    async emitEbookEvent(k, content, position) {
        const timestamp = new Date();
        const eventData = {
            timestamp: timestamp,
            title: content.name,
            asset_id: content.id.toString(),
            total_length: 1,
        };
        if (!eventData.asset_id)
            return;
        if (position === undefined) {
            console.warn(`No position for ContentEvents.${k}. Can't track content played.`);
        }
        const playread = this.trackEbookRead(content.id.toString(), timestamp, position, k === "pausing");
        if (playread) {
            Object.assign(eventData, playread);
        }
        try {
            return await this.emitContentEvent(k, {
                timestamp,
                content,
                ...eventData,
            });
        }
        catch (err) {
            console.error(`Failed to emit ContentEvents.${k}`, err);
        }
    }
    async emitContentEvent(k, { timestamp, content, ...properties } = {}) {
        try {
            const event = {
                event: k === "heartbeat" || k === "pausing"
                    ? ContentEventNames["playing"]
                    : ContentEventNames[k],
                timestamp: timestamp || new Date(),
                properties: {
                    ...(content && {
                        asset_id: String(content.id),
                        title: content.name,
                    }),
                    ...properties,
                },
            };
            const { asset_id } = event.properties;
            if (!asset_id) {
                console.warn(`No asset id for ContentEvents.${k}`);
                return;
            }
            if (!event.properties.session_id) {
                const { sessionId } = usePlaylistStateStore.getState();
                event.properties.session_id = sessionId;
            }
            const lastContentEvent = this.getLastContentEvent(event.properties);
            if (k === "started") {
                if (lastContentEvent) {
                    event.event = ContentEventNames["playing"];
                }
            }
            else if (k === "heartbeat") {
                if (this.playingEventInterval > 0) {
                    const lastEventTime = lastContentEvent?.timestamp?.getTime();
                    if (lastEventTime &&
                        event.timestamp.getTime() - this.playingEventInterval <
                            lastEventTime) {
                        return;
                    }
                }
            }
            if (event.properties.position === undefined &&
                content?.asset?.type === FileType.AUDIO) {
                const progress = await TrackPlayer.getProgress();
                event.properties.position = progress.position;
            }
            else if (event.properties.position === undefined &&
                content?.asset?.type === FileType.EBOOK) {
                const playtimeInfo = usePlaybackStateStore.getState().playtimeInfo;
                if (playtimeInfo?.id === content?.id.toString()) {
                    event.properties.position = playtimeInfo?.position;
                }
            }
            if (content?.asset?.type !== FileType.EBOOK) {
                if (event.properties.position === undefined) {
                    console.warn(`No position for ContentEvents.${k}. Can't track content played.`);
                }
                const playtime = this.trackPlaytime(asset_id, event.timestamp, event.properties.position, k === "pausing");
                if (playtime) {
                    Object.assign(event.properties, playtime);
                }
            }
            usePlaybackStateStore.getState().setLastContentEvent(event);
            if (content?.asset?.type === FileType.EBOOK &&
                event.properties?.time_played &&
                event.properties.time_played < 1 &&
                event.event === ContentEventNames.playing) {
                return;
            }
            await this.emitEvent(event);
        }
        catch (err) {
            console.error(`Failed to emit ContentEvents.${k}`, err);
        }
    }
    trackPlaytime(id, timestamp, position, isPause = false) {
        const { playtimeInfo } = usePlaybackStateStore.getState();
        usePlaybackStateStore.getState().setPlaytimeInfo({
            id,
            isPaused: isPause,
            position,
            timestamp,
        });
        let playtime;
        if (playtimeInfo && playtimeInfo.id === id && !playtimeInfo.isPaused) {
            const time_played = (timestamp.getTime() - playtimeInfo.timestamp.getTime()) / 1000;
            if (position === undefined || playtimeInfo.position === undefined) {
                playtime = { time_played };
            }
            else {
                const dPosition = position - playtimeInfo.position;
                const positiveDPosition = dPosition > 0 ? dPosition : 0;
                playtime = {
                    time_played,
                    content_played: positiveDPosition,
                };
            }
        }
        return playtime?.time_played ? playtime : undefined;
    }
    trackEbookRead(id, timestamp, position, isPause = false) {
        const { playtimeInfo } = usePlaybackStateStore.getState();
        usePlaybackStateStore.getState().setPlaytimeInfo({
            id,
            isPaused: isPause,
            position: position?.locations?.totalProgression,
            timestamp,
        });
        let playread;
        if (playtimeInfo && playtimeInfo.id === id && !playtimeInfo.isPaused) {
            const time_played = (timestamp.getTime() - playtimeInfo.timestamp.getTime()) / 1000;
            let currentTotalProgression = position?.locations?.totalProgression;
            if (currentTotalProgression === undefined) {
                const playtimeInfo = usePlaybackStateStore.getState().playtimeInfo;
                if (playtimeInfo?.id === id) {
                    currentTotalProgression = playtimeInfo?.position;
                }
            }
            if (!position?.locations ||
                currentTotalProgression === undefined ||
                playtimeInfo.position === undefined) {
                playread = { time_played };
            }
            else {
                const dPosition = currentTotalProgression - playtimeInfo.position;
                const positiveDPosition = dPosition > 0 ? dPosition : 0;
                playread = {
                    time_played,
                    content_read: positiveDPosition,
                    ebook_file: position?.href,
                    ebook_file_progression: position.locations.progression,
                    ebook_position: position.locations.position,
                };
            }
        }
        return playread;
    }
    async onStateChange(event) {
        const { state } = event;
        if (state === State.Buffering) {
        }
        else if (state === State.Error) {
            await this.emitAudioEvent("pausing");
            await this.emitPlaybackEvent("interrupted", {
                method: event.error.message,
            });
        }
        if (state === State.Playing) {
            await this.emitAudioEvent("started");
        }
        else if (state === State.Ended) {
            await this.emitAudioEvent("completed");
        }
    }
    async emitEvent({ event, timestamp, properties, }) {
        const userId = await getUUID();
        const message = {
            topic: event,
            payload: {
                ...properties,
                timestamp: (timestamp || new Date()).toISOString(),
                userId,
            },
        };
        console.debug(`Emitting ${event} event`, message);
        await AnalyticsEventEmitter.emit(event, message);
    }
    async getPlaybackInfo() {
        const { sessionId } = usePlaylistStateStore.getState();
        const { content, states } = usePlaybackStateStore.getState();
        const progress = content?.asset?.type === FileType.AUDIO
            ? await TrackPlayer.getProgress()
            : undefined;
        return {
            session_id: sessionId,
            content_asset_id: content?.id.toString(),
            position: progress?.position,
            total_length: progress?.duration,
        };
    }
    getLastContentEvent(currentProps) {
        const { lastContentEvent } = usePlaybackStateStore.getState();
        if (lastContentEvent && currentProps) {
            const { properties } = lastContentEvent;
            if (properties.session_id !== currentProps.session_id ||
                properties.asset_id !== currentProps.asset_id ||
                properties.pod_id !== currentProps.pod_id) {
                return undefined;
            }
        }
        return lastContentEvent;
    }
    async getAudioInfo() {
        const { sessionId } = usePlaylistStateStore.getState();
        const track = await TrackPlayer.getActiveTrack();
        const playSpeed = (await TrackPlayer.getRate()) || 1;
        return {
            session_id: sessionId,
            asset_id: track?.id?.toString(),
            title: track?.title,
            genre: track?.genre,
            total_length: track?.duration,
            play_speed: playSpeed,
        };
    }
}
const analytics = new PlayerAnalytics();
export default analytics;
//# sourceMappingURL=analytics.js.map